<template>
    <b-card class="w-100" no-body>
        <b-card-body class="pt-2">
            <b-row>
                <b-col>
                    <slot name="above-search"> </slot>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <slot name="search-input" v-if="includeSearchInput && filters && filters.options">
                        <b-input-group>
                            <b-form-input size="md" ref="searchInput" name="automatic" type="search" hide-label
                                :placeholder='searchMessageComputed' v-model="filters.automatic" :debounce-wait="300"
                                @keydown.enter.prevent="" />
                            <p-select class="ms-2" v-model="filters.searchBy" :options="filters.options" />
                        </b-input-group>
                    </slot>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <slot name="above-table"> </slot>
                </b-col>
            </b-row>
            <b-row>
                <b-table ref="searchResults" v-if="locationId" v-bind="$attrs" responsive striped hover small show-empty sort-icon-left
                    no-local-sorting no-local-filtering no-local-paging
                    :busy="isBusy"
                    must-sort
                    :empty-text="computedEmptyText"
                    :items="tableItems" :fields="calculatedFields"
                    v-model:sort-by="bvnSortBy"
                    :tbody-tr-class="computedRowClass"
                    @row-clicked="
                        (item) => {
                            if (enableRowSelection && !disableRowCheck(item)) {
                                toggleRowSelection(item);
                                rowClicked(item);
                            }
                        }
                    " @row-selected="$emit('row-selected', $event)"
                    @context-changed="updateSelectAll" @refreshed="updateSelectAll" @input="itemsChanged"
                    class="">
                    <template #thead-top v-if="showTopPaging">
                        <b-tr>
                            <b-th :colspan="calculatedFields.length" class="pe-0">
                                <b-col class="d-flex pb-0 pt-0 pe-0">
                                    <div class="ms-auto me-3">
                                        <p-select v-model="perPageSelection" :options="perPageOptions" class="per-page-select" v-if="
                                            showPerPageSelector &&
                                            totalRows > minPerPage
                                        ">
                                        </p-select>
                                    </div>
                                    <b-pagination v-model="currentPage" :total-rows="totalRows" label="Per Page"
                                        :per-page="perPageSelection"></b-pagination>
                                </b-col>
                            </b-th>
                        </b-tr>
                    </template>
                    <template v-slot:table-busy>
                        <div class="text-center text-primary my-2">
                            <b-spinner variant="primary" class="align-middle"></b-spinner>
                            <strong class="ms-2">Loading...</strong>
                        </div>
                    </template>
                    <template v-for="(_, name) in $slots" :key="name" v-slot:[name]="slotData">
                        <slot :name="name" v-bind="{ ...slotData, hasSearched }" />
                    </template>
                    <template v-slot:head(selected)="data">
                        <p-checkbox v-if="!singleSelect" :model-value="allSelected" @change="toggleSelectAll"
                            :label="selectionLabel"></p-checkbox>
                        <span v-else>{{ singleSelectButtonLabel }}</span>
                    </template>
                    <template v-slot:cell(selected)="{ value, item }">
                        <p-checkbox v-if="!singleSelect" :model-value="isSelected(item)" @change="onSelection(item)"
                            :disabled="disableRowCheck(item)"></p-checkbox>
                        <p-button v-else size="sm" @click="onAddSelection(item)">{{ singleSelectButtonLabel
                            }}</p-button>
                    </template>
                    <template v-slot:cell(dualSelected)="{ value, item }">
                        <p-button size="sm" variant="outline-primary" @click="onAddSelection(item)"
                            :disabled="disableSingleSelectButton">{{
                                singleSelectButtonLabel
                            }}</p-button>
                    </template>
                    <!-- putting the bottom pagination in the caption slot allows
                         it to be included in the table's busy spinner overlay -->
                    <template #table-caption v-if="showBottomPaging">
                        <b-row class="mt-3">
                            <b-col class="d-flex py-0 pe-0 ">
                                <div class="mt-2">{{ totalRows }} Results</div>
                                <div class="ms-auto me-3">
                                    <p-select v-model="perPageSelection"
                                        v-if="showPerPageSelector && totalRows > minPerPage"
                                        :options="perPageOptions" class="per-page-select" />
                                </div>
                                <b-pagination v-if="showBottomPaging" v-model="currentPage" :total-rows="totalRows"
                                    label="Per Page" :per-page="perPageSelection"></b-pagination>
                            </b-col>
                        </b-row>
                    </template>
                </b-table>
            </b-row>
        </b-card-body>
    </b-card>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { searchPlaceHolderFormatter } from './Common/Formatters';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';

export default {
    name: 'p-search-table',
    inheritAttrs: false,
    emits: ['row-selected', 'per-page-change', 'sort-by-change', 'sort-desc-change', 'row-clicked', 'filtered', 'is-table-loading', 'total-rows-changed', 'update:modelValue', 'input', 'submit-selection', 'selected-rows-changed'],
    props: {
        modelValue: { default: null },
        fields: Array,
        perPage: {
            type: Number,
            default: 10
        },
        perPageOptions: {
            type: Array,
            default: () => [
                { value: 10, text: 'Show 10' },
                { value: 25, text: 'Show 25' },
                { value: 50, text: 'Show 50' },
                { value: 100, text: 'Show 100' },
                { value: 500, text: 'Show 500' }
            ]
        },
        includeSearchInput: {
            type: Boolean,
            default: true
        },
        showPerPageSelector: {
            type: Boolean,
            default: true
        },
        //can probably remove showTopPaging prop when we add PerPageSelector to edit pages.
        //Added to keep existing behavior for now, and I don't think the top paging is needed
        //when they are stuck at 10 per page because the top and bottom paging controls are both always visible.
        showTopPaging: {
            type: Boolean,
            default: true
        },
        showBottomPaging: {
            type: Boolean,
            default: true
        },
        selectable: {
            type: Boolean,
            default: false
        },
        selectMode: {
            type: String,
            default: 'range'
        },
        sortBy: String,
        emptyText: String,
        sortDesc: Boolean,
        filters: Object,
        apiDestination: String,
        componentIsLoading: Boolean,
        searchDestination: {
            type: String,
            default: null
        },
        submitSearchAsPost: {
            type: Boolean,
            default: false
        },
        singleSelect: {
            type: Boolean,
            default: false
        },
        singleSelectButtonLabel: {
            type: String,
            default: 'Add'
        },
        disableRowCheck: {
            type: Function,
            default: () => false
        },
        enableRowSelection: {
            type: Boolean,
            default: true
        },
        enableDualSelection: {
            type: Boolean,
            default: false
        },
        // todo: when we get rid of bootstrap select we should get rid of this property
        useBootstrapSelection: {
            type: Boolean,
            default: false
        },
        disableInitialSearch: {
            type: Boolean,
            default: false
        },
        editKey: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            currentPage: 1,
            totalRows: 0,
            tableSortBy: this.sortBy,
            tableSortDesc: this.sortDesc,
            bvnSortBy: this.sortBy ? [{ key: this.sortBy, order: this.sortDesc ? 'desc' : 'asc' }] : [],
            allSelected: false,
            selectedRows: [],
            perPageSelection: 10,
            sorting: false,
            locationId: null,
            tableItems: [],
            isBusy: false,
            hasSearched: false,
            lastEmittedSortKey: this.sortBy ?? null,
            lastEmittedSortDesc: !!this.sortDesc
        };
    },
    created() {
        this.perPageSelection = this.filters?.perPage ?? this.perPage;
        // Non-reactive counter used by fetchItems() to drop stale responses
        // when multiple fetches overlap (e.g. initial mount + filter watcher).
        this._fetchRequestId = 0;
        // Coalesce bursts of filter mutations (common when a parent sets several
        // filter fields back-to-back, e.g. modal initialize) into a single fetch.
        this.debouncedRefresh = debounce(() => {
            if (!this.sorting) {
                this.refreshSearchResults();
            }
            this.sorting = false;
        }, 50);
    },
    async beforeMount() {
        this.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
        this.fetchItems();
    },
    watch: {
        filters: {
            handler: function() {
                this.debouncedRefresh();
            },
            deep: true
        },
        searchUrl: {
            handler: function () {
                this.goToFirstPage();
            }
        },
        perPageSelection: {
            handler: function (newValue) {
                this.$emit('per-page-change', newValue);
                this.fetchItems();
            }
        },
        currentPage() {
            this.fetchItems();
        },
        bvnSortBy: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    const sort = newVal[0];
                    this.tableSortBy = sort.key;
                    this.tableSortDesc = sort.order === 'desc';
                } else {
                    this.tableSortBy = null;
                    this.tableSortDesc = false;
                }
                // Always track the latest header the user clicked so the wheel
                // state stays current, but only actually fire one fetch — if a
                // previous one is in flight, defer until it returns. The
                // isBusy watcher below picks it up.
                this.flushSortChange();
            },
            deep: true
        },
        isBusy(newVal) {
            // Previous fetch finished — if the user clicked more headers while
            // waiting, the latest one (already in tableSortBy) gets flushed now.
            if (!newVal) this.flushSortChange();
        },
    },
    computed: {
        value() { return this.modelValue; },
        computedFilters() {
            return { locationId: this.locationId, ...this.filters,};
        },
        searchMessageComputed() {
            return searchPlaceHolderFormatter(this.filters.options, this.filters.searchBy);
        },
        minPerPage() {
            return this.perPageOptions[0].value;
        },
        searchUrl() {
            var destination = '/search';
            if (this.searchDestination) {
                destination = '/' + this.searchDestination;
            }
            return this.apiDestination + destination;
        },
        selectionLabel() {
            return this.selectedRows.length > 0
                ? `(${this.selectedRows.length})`
                : '';
        },
        visibleFields() {
            return (this.fields || []).filter(x => !x.hidden).map(f => {
                // Auto-center boolean toggle columns (isActive / active) so the
                // header label and cell content align with the centered checkbox.
                // Respect any explicit thClass/tdClass the caller already set.
                const key = (f.key || '').toString().toLowerCase();
                if (key === 'isactive' || key === 'active') {
                    return {
                        ...f,
                        thClass: f.thClass ?? 'text-center',
                        tdClass: f.tdClass ?? 'text-center'
                    };
                }
                return f;
            });
        },
        computedEmptyText() {
            if (this.filters?.automatic == "" && this.disableInitialSearch) {
                return "Enter filtering criteria to see matching results";
            }
            return this.emptyText;
        },
        calculatedFields() {
            if (this.enableRowSelection && !this.useBootstrapSelection) {

                if (this.enableDualSelection) {
                    return [
                        {
                            label: '',
                            key: 'selected',
                            thStyle: { width: '40px' }, 
                            tdClass: 'fixed-width',
                            sortable: false
                        },
                        ...this.visibleFields,
                        {
                            label: 'Quick Add',
                            key: 'dualSelected',
                            sortable: false
                        }
                    ];
                } else {
                    return [
                        {
                            label: '',
                            key: 'selected',
                            thStyle: { width: '40px' }, 
                            tdClass: 'fixed-width',
                            sortable: false
                        },
                        ...this.visibleFields
                    ];
                }
            }

            return this.visibleFields;
        },
        disableSingleSelectButton() {
            return this.selectedRows.length > 0;
        }
    },
    methods: {
        flushSortChange() {
            if (this.isBusy) return;
            // Treat undefined and null sort keys as equal. A table with no sortBy
            // initializes tableSortBy=undefined but lastEmittedSortKey=null, so
            // without this normalization the first flushSortChange on load reads
            // as a "sort change" and sets the `sorting` gate true — which then
            // swallows the user's first filter change (e.g. the first click of a
            // Show Inactive checkbox).
            if ((this.tableSortBy ?? null) === (this.lastEmittedSortKey ?? null)
                && this.tableSortDesc === this.lastEmittedSortDesc) return;
            this.lastEmittedSortKey = this.tableSortBy;
            this.lastEmittedSortDesc = this.tableSortDesc;
            // The emits below cause the parent to write filters.sortBy/sortDesc,
            // which would re-trigger the deep filters watcher → debouncedRefresh
            // and fire a second fetch. Set the gate the debounced callback checks.
            this.sorting = true;
            this.$emit('sort-by-change', this.tableSortBy);
            this.$emit('sort-desc-change', this.tableSortDesc);
            this.fetchItems();
        },
        computedRowClass(item, type) {
            if (type === 'row' && item && this.disableRowCheck?.(item)) {
                return 'p-table-row-disabled';
            }
            return null;
        },
        toggleRowSelection(item) {
            this.onSelection(item);
        },
        goToFirstPage() {
            this.currentPage = 1;
        },
        rowClicked(element) {
            this.$emit('row-clicked', element);
        },
        refreshSearchResults() {
            // If currentPage actually changes, the currentPage watcher fetches.
            // If it was already 1, the watcher won't fire — we have to fetch ourselves.
            if (this.currentPage !== 1) {
                this.currentPage = 1;
            } else {
                this.fetchItems();
            }
            this.$emit('filtered');
        },
        getQueryParameters: function () {
            const pagination = {
                currentPage: this.currentPage,
                perPage: this.perPageSelection,
                sortBy: this.tableSortBy,
                sortDesc: this.tableSortDesc
            };

            return { ...this.computedFilters, ...pagination };
        },
        async fetchItems() {
            // Per-fetch token. Overlapping calls (initial mount + filter watcher
            // firing as the parent populates state) used to race: the fast one
            // would finish first, flip isBusy=false, and the table would show
            // "no records" until the slow one returned. Now stale responses
            // are dropped and only the latest fetch can touch state.
            const requestId = ++this._fetchRequestId;
            const isLatest = () => requestId === this._fetchRequestId;

            this.$emit('is-table-loading', true);
            this.isBusy = true;
            if (!this.locationId) {
                return;
            }
            if (this.componentIsLoading) {
                if (isLatest()) {
                    this.totalRows = 0;
                    this.$emit('total-rows-changed', 0);
                    this.tableItems = [];
                }
                return;
            }
            if (this.filters?.automatic == "" && this.disableInitialSearch) {
                if (isLatest()) {
                    this.totalRows = 0;
                    this.$emit('total-rows-changed', 0);
                    this.$emit('is-table-loading', false);
                    this.tableItems = [];
                    this.isBusy = false;
                }
                return;
            }
            try {
                let resp;
                if (this.submitSearchAsPost) {
                    resp = await axios.post(this.searchUrl, {
                        ...this.getQueryParameters()
                    });
                } else {
                    resp = await axios.get(this.searchUrl, {
                        params: this.getQueryParameters()
                    });
                }
                if (!isLatest()) return; // a newer fetch superseded us
                this.totalRows = resp.data.count;
                this.$emit('total-rows-changed', resp.data.count);
                this.tableItems = resp.data.data || [];
                this.hasSearched = true;
            } catch {
                if (isLatest()) this.tableItems = [];
            } finally {
                if (isLatest()) {
                    this.$emit('is-table-loading', false);
                    this.isBusy = false;
                }
            }
        },
        updateSelectAll() {
            const items = this.tableItems || [];
            const selectable = items.filter(x => !this.disableRowCheck?.(x));
            this.allSelected = selectable.length > 0 && selectable.every(elem => this.isSelected(elem));
        },
        toggleSelectAll() {
            const items = this.tableItems || [];
            const selectable = items.filter(x => !this.disableRowCheck?.(x));
            if (!this.allSelected) {
                this.selectedRows = [...selectable];
            } else {
                this.selectedRows = [];
            }
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
            this.updateSelectAll();
        },
        isSelected(item) {
            return this.selectedRows.some(
                x => JSON.stringify(x) === JSON.stringify(item)
            );
        },
        onSelection(item) {
            var existingIndex = this.selectedRows.findIndex(
                x => JSON.stringify(x) === JSON.stringify(item)
            );
            if (existingIndex > -1) {
                this.selectedRows.splice(existingIndex, 1);
            } else {
                this.selectedRows.push(item);
            }
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
            this.updateSelectAll();
        },
        onAddSelection(item) {
            this.onSelection(item);
            this.$emit('submit-selection');
        },
        itemsChanged(item) {
            let items = Array.isArray(item) ? item : this.tableItems || [];
            let selectedCount = this.selectedRows.length;
            this.selectedRows = this.selectedRows.filter(s => items.findIndex(t => t[this.editKey] == s[this.editKey]) !== -1);
            this.updateSelectAll();
            if (selectedCount != this.selectedRows.length) {
                this.$emit('selected-rows-changed', this.selectedRows);
            }
        },
        clearSelectedRows() {
            this.selectedRows = [];
            this.allSelected = false;
        }
    }
};
</script>


<style scoped lang="scss">
@import "@/styles/app/common/variables.scss";

:deep(.right-align ) {
    text-align: right;
}

:deep(th ) {
    user-select: none;
}

/* Keep sort arrow on the same line as the header text instead of wrapping below. */
:deep(thead th),
:deep(thead th *) {
    white-space: nowrap !important;
}

:deep(td) {
    vertical-align: middle;
}

:deep(caption ) {
    width: 98%; //avoids horizontal scrollbar
    color: $body-color;
}

:deep(.fixed-width ) {
    width: 40px !important;
    white-space: nowrap;
}

:deep(tr.b-table-row-selected) {
    background-color: rgba($primary, 0.2) !important;
    color: $body-color !important;
}
:deep(tr.b-table-row-selected td) {
    background-color: rgba($primary, 0.2) !important;
    color: $body-color !important;
}
:deep(.table-hover tbody tr:hover td) {
    background-color: var(--m-surface-sunken, #{$gray-lighter}) !important;
}
:deep(tr.p-table-row-disabled) > td {
    opacity: 0.6;
    cursor: not-allowed;
}
:deep(.table-hover tbody tr.p-table-row-disabled:hover) > td {
    background-color: transparent !important;
}

</style>
<style lang="scss">
.form-select.per-page-select {
    min-width: 115px;
}
</style>